import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, LinearProgress, CircularProgress, Snackbar } from '@mui/material';
import UpdateOutlinedIcon    from '@mui/icons-material/UpdateOutlined';
import ScheduleOutlinedIcon  from '@mui/icons-material/ScheduleOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SyncIcon              from '@mui/icons-material/Sync';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import PlayCircleOutlineIcon  from '@mui/icons-material/PlayCircleOutline';

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

function useRelative(dateStr) {
    const [label, setLabel] = useState('');
    useEffect(() => {
        if (!dateStr) { setLabel(''); return; }
        const compute = () => {
            const diff = Date.now() - new Date(dateStr).getTime();
            const abs  = Math.abs(diff);
            const past = diff > 0;
            if (abs < 60_000)     return past ? 'just now'                                  : 'in a moment';
            if (abs < 3_600_000)  return past ? `${Math.round(abs / 60_000)} min ago`       : `in ${Math.round(abs / 60_000)} min`;
            if (abs < 86_400_000) return past ? `${Math.round(abs / 3_600_000)} hr ago`     : `in ${Math.round(abs / 3_600_000)} hr`;
            return past ? `${Math.round(abs / 86_400_000)} days ago` : `in ${Math.round(abs / 86_400_000)} days`;
        };
        setLabel(compute());
        const id = setInterval(() => setLabel(compute()), 30_000);
        return () => clearInterval(id);
    }, [dateStr]);
    return label;
}

const Pill = ({ icon: Icon, color, label, value }) => (
    <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        px: 1.5, py: 0.6, borderRadius: '20px',
        bgcolor: `${color}0D`, border: `1px solid ${color}26`,
    }}>
        <Icon sx={{ fontSize: 13, color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 11.5, color: '#52514E', fontWeight: 500 }}>{label}</Typography>
        <Typography sx={{ fontSize: 11.5, color, fontWeight: 700 }}>{value}</Typography>
    </Box>
);

const ActionBtn = ({ onClick, disabled, loading, icon: Icon, label, color, borderColor, hoverBg }) => (
    <Button
        size="small"
        onClick={onClick}
        disabled={disabled}
        startIcon={loading
            ? <CircularProgress size={12} sx={{ color }} />
            : <Icon sx={{ fontSize: 14 }} />
        }
        sx={{
            fontSize: 11.5, fontWeight: 600, px: 1.5, py: 0.5,
            borderRadius: '20px', textTransform: 'none', lineHeight: 1.4,
            color, border: `1px solid ${borderColor}`,
            bgcolor: 'transparent',
            '&:hover': { bgcolor: hoverBg, borderColor: color },
            '&.Mui-disabled': { opacity: 0.45 },
        }}
    >
        {label}
    </Button>
);

const RefreshInfoBar = ({
    lastUpdatedAt,
    lastManualRefreshAt,
    nextRefreshAt,
    isRefreshing,
    autoRefreshStopped  = false,
    onUpdateNow,
    onStopUpdates,
    onResumeUpdates,
    isManualRefreshing  = false,
    refreshProgress     = null,
    isPublic            = false,
}) => {
    const lastLabel = useRelative(lastUpdatedAt);
    const nextLabel = useRelative(nextRefreshAt);
    const [doneFlash, setDoneFlash] = useState(false);
    const [cooldownSnack, setCooldownSnack] = useState(false);

    // Cooldown: disabled if data was refreshed within 2 hours by ANY means (auto or manual).
    // Takes the most recent of lastUpdatedAt and lastManualRefreshAt so a fresh auto-cadence
    // run also locks the button — no point forcing a re-fetch on just-updated data.
    const lastRefresh = [lastUpdatedAt, lastManualRefreshAt]
        .filter(Boolean)
        .map(d => new Date(d).getTime())
        .reduce((max, t) => Math.max(max, t), 0);
    const msSinceUpdate = lastRefresh > 0 ? Date.now() - lastRefresh : Infinity;
    const isCooldown = msSinceUpdate < TWO_HOURS_MS;
    const cooldownMinsLeft = isCooldown ? Math.ceil((TWO_HOURS_MS - msSinceUpdate) / 60_000) : 0;
    const cooldownLabel = cooldownMinsLeft >= 60
        ? `${Math.ceil(cooldownMinsLeft / 60)} hr`
        : `${cooldownMinsLeft} min`;

    const handleUpdateNow = () => {
        if (isCooldown) { setCooldownSnack(true); return; }
        onUpdateNow?.();
    };

    // Show a brief "Done!" flash when manual refresh completes
    useEffect(() => {
        if (refreshProgress?.isComplete) {
            setDoneFlash(true);
            const t = setTimeout(() => setDoneFlash(false), 2500);
            return () => clearTimeout(t);
        }
    }, [refreshProgress?.isComplete]);

    const hasAnything = lastUpdatedAt || nextRefreshAt || isRefreshing || isManualRefreshing || !isPublic;
    if (!hasAnything) return null;

    return (
        <Box sx={{ mb: 2 }}>
            {/* Row 1: status pills + action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                {lastUpdatedAt && (
                    <Pill icon={UpdateOutlinedIcon} color="#7C6FF7" label="Last updated" value={lastLabel} />
                )}

                {nextRefreshAt && !isRefreshing && !autoRefreshStopped && (
                    <Pill icon={ScheduleOutlinedIcon} color="#FF8C00" label="Next update" value={nextLabel} />
                )}

                {isRefreshing && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        px: 1.5, py: 0.6, borderRadius: '20px',
                        bgcolor: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
                    }}>
                        <FiberManualRecordIcon sx={{ fontSize: 8, color: '#22C55E', animation: 'pulse 1.4s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } } }} />
                        <Typography sx={{ fontSize: 11.5, color: '#16A34A', fontWeight: 600 }}>Updating now…</Typography>
                    </Box>
                )}

                {autoRefreshStopped && !isRefreshing && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        px: 1.5, py: 0.6, borderRadius: '20px',
                        bgcolor: 'rgba(156,163,175,0.08)', border: '1px solid rgba(156,163,175,0.25)',
                    }}>
                        <StopCircleOutlinedIcon sx={{ fontSize: 12, color: '#9CA3AF' }} />
                        <Typography sx={{ fontSize: 11.5, color: '#6B7280', fontWeight: 600 }}>Auto-updates stopped</Typography>
                    </Box>
                )}

                {/* Action buttons — owner only */}
                {!isPublic && (
                    <>
                        {/* Update Now — only visible when auto-refresh is active */}
                        {!autoRefreshStopped && (
                            <ActionBtn
                                onClick={handleUpdateNow}
                                disabled={isManualRefreshing || isRefreshing}
                                loading={isManualRefreshing}
                                icon={SyncIcon}
                                label={isManualRefreshing ? 'Updating…' : 'Update Now'}
                                color={isCooldown ? '#9CA3AF' : '#FF5200'}
                                borderColor={isCooldown ? 'rgba(156,163,175,0.3)' : 'rgba(255,82,0,0.35)'}
                                hoverBg={isCooldown ? 'rgba(156,163,175,0.06)' : 'rgba(255,82,0,0.06)'}
                            />
                        )}

                        {autoRefreshStopped ? (
                            <ActionBtn
                                onClick={onResumeUpdates}
                                disabled={false}
                                loading={false}
                                icon={PlayCircleOutlineIcon}
                                label="Resume Updates"
                                color="#16A34A"
                                borderColor="rgba(22,163,74,0.35)"
                                hoverBg="rgba(22,163,74,0.06)"
                            />
                        ) : (
                            <ActionBtn
                                onClick={onStopUpdates}
                                disabled={false}
                                loading={false}
                                icon={StopCircleOutlinedIcon}
                                label="Stop Future Updates"
                                color="#6B7280"
                                borderColor="rgba(107,114,128,0.3)"
                                hoverBg="rgba(107,114,128,0.06)"
                            />
                        )}
                    </>
                )}
            </Box>

            {/* Row 2: progress bar during manual refresh */}
            {(isManualRefreshing || doneFlash) && refreshProgress && (
                <Box sx={{ mt: 1.5, px: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Typography sx={{ fontSize: 11.5, color: '#52514E', fontWeight: 600 }}>
                            {doneFlash && refreshProgress.isComplete
                                ? '✓ Done! Charts updated.'
                                : `Refreshing ${refreshProgress.processed ?? 0} of ${refreshProgress.total ?? 0} posts…`
                            }
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#FF5200', fontWeight: 700 }}>
                            {refreshProgress.percentage ?? 0}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={refreshProgress.percentage ?? 0}
                        sx={{
                            height: 5, borderRadius: 3,
                            bgcolor: 'rgba(255,82,0,0.10)',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                background: 'linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)',
                                transition: 'transform 0.4s ease',
                            },
                        }}
                    />
                </Box>
            )}
            <Snackbar
                open={cooldownSnack}
                autoHideDuration={4000}
                onClose={() => setCooldownSnack(false)}
                message={`You can only update once every 2 hours. Try again in ${cooldownLabel}.`}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{ sx: { bgcolor: '#1C1B18', fontSize: 13, fontWeight: 500, borderRadius: '10px' } }}
            />
        </Box>
    );
};

export default RefreshInfoBar;
